/**
 * 本文件是系统公用函数模块
 * @author 笑三少
 */
import {
  axios_get,
  axios_post,
  axios_put,
  axios_del,
  axios_download
} from './RequestUtils';
import {
  showError,
  showMessage,
  showConfirm,
  successMessage,
  errorMessage,
  redirectToLogin
} from '@/utils'

import {
  SelectOptionItem,
  TableDataItem,
} from '@/api'

import { LOGIN_PATH } from '@/config/setting'
import dayjs from 'dayjs'
// ========== 分页数据每页显示记录条数 ===========
const PAGE_SIZE = import.meta.env.VITE_PAGE_SIZE ? Number(import.meta.env.VITE_PAGE_SIZE) : 13

// ========== 通用获取数据函数 ==========
/**
 * @param url:后台数据接口
 * @param params:参数列表
 * @param errorMessage:查询失败错误消息提示
 * @param defaultValue:默认后端返回数据结构
 * @param showError:是否显示错误消息
 * @param method:默认为post
 */
const getAxiosData = async <T = any>({
                                       url,
                                       params = {},
                                       errorMessage = '数据获取失败',
                                       defaultValue = {
                                         status: 0,
                                         data:''
                                       },
                                       showError = true,
                                       method = 'post'
                                     }: RequestConfig): Promise<ResponseItem<T> | null> => {
  try {
    let result:ResponseItem<T>
    // 根据方法选择不同的请求函数
    switch (method) {
      case 'get':
        result = await axios_get(url, params)
        break
      case 'post':
        result = await axios_post(url, params)
        break
      case 'put':
        result = await axios_put(url, params)
        break
      case 'delete':
        result = await axios_del(url, params)
        break
      default:
        result = await axios_post(url, params)
    }
    if(result.status>1){
      return {
        status: result.status,
        data: (typeof result.data === 'string' ? result.data : errorMessage) as any
      }
    }
    return result
  } catch (error: any) {
    //保留实际状态码
    return {
      status: error?.status || 0,
      data: (error?.data || errorMessage) as any
    }
  }
}

// ========== 获取选择器选项列表 ==========
/**
 * @param url:后端接口
 * @param labelKey:选项名称
 * @param valueKey:选项值
 * @param params:参数列表
 * @param errorMessage:错误消息提示
 * @param defaultOption:默认选项
 * @param disabledKey:禁用选项
 */
const getSelectOptions = async ({
                                  url,
                                  labelKey = 'name',
                                  valueKey = 'id',
                                  params = {},
                                  errorMessage = '获取选项失败',
                                  defaultOption = null,      // 默认选项，如 { label: '全部', value: '' }
                                  disabledKey = 'disabled',  // 禁用字段
                                }: {
  url: string
  labelKey?: string
  valueKey?: string | string[]
  params?: object
  errorMessage?: string
  defaultOption?: SelectOptionItem | null
  disabledKey?: string
} = {}): Promise<SelectOptionItem[] | null> => {
  try {
    const result = await getAxiosData<ResponseItem>({
      url,
      params,
      errorMessage,
      defaultValue: []
    })
    let options: SelectOptionItem[] = []
    if (result && result.status) {
      options = result.data.map(item => ({
        label: String(item[labelKey]),
        value: Array.isArray(valueKey)
          ? valueKey.map(key => item[key]).join(',')  // 多字段组合
          : String(item[valueKey]),                    // 单字段取值
        disabled: item[disabledKey] || false,
      }))
    }
    // 添加默认选项
    if (defaultOption) options.unshift(defaultOption)
    return options
  } catch (error) {return defaultOption ? [defaultOption] : []}
}

// ========== 获取选择器下拉列表项 ==========
/**
 * @param url 后端接口地址
 * @param labelKey 下拉列表项标签值
 * @param valueKey 下拉列表项值
 * @param errorMessage 错误提示
 * @param defaultOption 默认选项
 */
export const getSelectList = async (
  {
    url='',
    labelKey='name',
    valueKey='id',
    errorMessage='获取列表失败',
    params={},
    defaultOption = null,
  }:{
    url: string
    labelKey: string
    valueKey: string | string[]
    errorMessage?: string
    params?:object,
    defaultOption?: SelectOptionItem | null
  }): Promise<SelectOptionItem[]> => {
  if(!url) return []
  return getSelectOptions({
    url:url,
    labelKey:labelKey,
    valueKey:valueKey,
    params:params,
    errorMessage:errorMessage,
    defaultOption:defaultOption
  })
}

// ========== 提取文本中的数字 ==========
/**
 * @param str 目标字符串
 */
const extractNumbers=(str: string): number[]=> {
  const matches = str.match(/\d+/g);
  return matches ? matches.map(Number) : [];
}

// ========== 递归找出label或者value ==========
/**
 * @param options options数组
 * @param item 需要查找的项目
 * @param type 类型:0:label,1:value
 */
function findOptionInTree(
  options: Array<{ label: string; value: string | number; options?: any[] }>,
  item: string | number,
  type: 0 | 1
): { label: string; value: string | number } | null {
  for (const opt of options) {
    // 检查当前层级
    const compareValue = type === 0 ? opt.label : opt.value;
    if (opt.value !== undefined && String(compareValue) === String(item)) return opt;
    // 递归查找子级
    if (opt.options && opt.options.length > 0) {
      const found = findOptionInTree(opt.options, item, type);
      if (found) return found;
    }
  }
  return null;
}

// ========== 通用获取所有选项 ==========
/**
 * @param options 所有选项
 */
function getAllOptions(options: any[]): any[] {
  const result: any[] = [];
  for (const opt of options) {
    result.push(opt);
    if (opt.options && opt.options.length > 0) result.push(...getAllOptions(opt.options));
  }
  return result;
}

// =========== 获取数据 ===========
/**
 * @param url 请求地址
 * @param params 参数
 */
export const getRequestData=async <T = any>(
  url: string,
  params = {},
  method:string|'post'): Promise<ResponseItem<T>> => {
  const result= await getAxiosData({
    url:url,
    params:params,
    method:method
  })
  if (result && result.status > 1) {
    const errorMsg = typeof result.data === 'string' ? result.data : `请求失败 (${result.status})`
    showError(errorMsg)
  }
  return result
}

// ========== 更新Form组件内select选择器的下拉选项 ==========
/**
 * @param formItems 传递的formItem数组（可能是ref对象或普通数组）
 * @param prop formItem数组中select组件的prop
 * @param options 设置的select下拉列表项（可能是ref对象或普通数组）
 */
export const updateFormSelect = async (
  formItems: any,
  prop: string,
  {
    url='',
    labelKey='name',
    valueKey='id',
    errorMessage='获取列表失败',
    params={},
    defaultOption = null,
  }:{
    url: string
    labelKey: string
    valueKey: string
    errorMessage?: string
    params?:object,
    defaultOption?: SelectOptionItem | null
  }): Promise<SelectOptionItem[]>=>{
  const result = await getSelectList({
    url:url,
    labelKey:labelKey,
    valueKey:valueKey,
    errorMessage:errorMessage,
    params:params,
    defaultOption:defaultOption
  })
  const realArray = Array.isArray(result) ? result : Array.from(result || [])
  // 处理formItems可能是ref对象的情况
  const items = formItems.value || formItems
  // 确保items是数组
  if (!Array.isArray(items)) return
  // 找到对应的表单项
  const formItem = items.find(item => item.prop === prop)
  // 检查是否是select类型
  if (formItem && formItem.type === 'select') formItem.options = realArray
  return realArray
}

// ========== 从本地数据中更新Form组件内select选择器的下拉选项 ==========
/**
 * @param formItems 传递的formItem数组（可能是ref对象或普通数组）
 * @param prop formItem数组中select组件的prop
 * @param selectOptions 本地选项列表
 * @returns 返回选项列表
 */
export const updateFormSelectFromLocal = (
  formItems: any,
  prop: string,
  selectOptions: SelectOptionItem[]
): SelectOptionItem[] => {
  // 处理formItems可能是ref对象的情况
  const items = formItems.value || formItems
  // 确保items是数组
  if (!Array.isArray(items) || !Array.isArray(selectOptions)) return selectOptions || []
  // 找到对应的表单项
  const formItem = items.find(item => item.prop === prop)
  // 检查是否是select类型
  if (formItem && formItem.type === 'select') formItem.options = selectOptions
  return selectOptions
}

// ========== 处理表格ICON图标事件 ==========
/**
 * @param config 基础配置
 */
export const handleTableOperation = async (config) => {
  const resultMessage=['操作成功','操作失败']
  if(!config.url) return
  config.successMessage = config.successMessage || resultMessage[0]
  config.errorMessage = config.errorMessage || resultMessage[1]
  try{
    if(config.confirmMessage){
      const action = await showConfirm(config.confirmMessage)
      if (!action) return
    }
    const result = await axios_post(config.url, config.params)
    if (result.status) {
      if(config.showMessage) successMessage(result.data || config.successMessage)
      if (config.onSuccess) config.onSuccess()
    }
    else {
      if(config.showMessage) errorMessage(result.data || config.errorMessage)
      if (config.onError) config.onError()
    }
  }catch(err){
    errorMessage(config.errorMessage)
  }
}

// ========== 获取下拉列表框选中的值 ==========
/**
 * @param selectValue 当前选中的选项(可以是单个值、逗号分隔的字符串或数组)
 * @param selectOptions 所有选项
 * @param type: 0获取label,1获取value
 * @returns 逗号分隔的字符串
 */
export const getSelectInfo = (
  selectValue: any,
  selectOptions: Array<{ label: string; value: string | number; options?: any[] }>,
  type: 0 | 1 = 0,
): string => {
  if (!Array.isArray(selectOptions) || selectOptions.length === 0) return '';
  let valueArray: (string | number)[] = [];
  if (selectValue === null || selectValue === undefined) valueArray = [];
  else if (Array.isArray(selectValue)) valueArray = selectValue;
  else if (typeof selectValue === 'string') valueArray = selectValue ? selectValue.split(',') : [];
  else valueArray = [selectValue];
  valueArray = valueArray.filter(item => item !== '' && item !== null && item !== undefined);
  const result: (string | number)[] = [];
  if (valueArray.length > 0) {
    valueArray.forEach(item => {
      const option = findOptionInTree(selectOptions, item, type);
      if (option) {
        // type: 0 返回 value, type: 1 返回 label
        result.push(type === 0 ? option.value : option.label);
      }
    });
  } else {
    const allOptions = getAllOptions(selectOptions);
    allOptions.forEach(option => {
      result.push(type === 0 ? option.value : option.label);
    });
  }
  return result.join(',');
}

// ========== 分页获取表格数据 ==========
/**
 * @param url api地址
 * @param params 请求参数
 */
export const getTablePageData= async<T = any> (
  url:string,
  params:object
):Promise<ResponseItem> => {
  if(!url) return {
    status:0,
    data:'未设置接口地址'
  }
  try {
    // 合并默认参数
    const requestParams = {
      ...params,
      currentPage: params.currentPage || 1,
      totalRecords: params.totalRecords || -1,
      pageSize: PAGE_SIZE
    }
    const result = await getAxiosData<ResponseItem>({
      url,
      params: requestParams,
      method: 'post',
      showError: false,
      defaultValue: { status: 0, data: null }
    })
    if (result?.status && result.data) {
      return {
        status:1,
        data:{
          totalRecords: result.data.totalRecords,
          currentPage: result.data.currentPage,
          records:result.data.resultRecords
        }
      }
    }
    return { status: 0, data: null }
  } catch (error) {
    return { status: 0, data: error || '请求错误' }
  }
}

// ========== 重置表格数据 ==========
export const resetTableData = () => {
  return {
    total: 0,
    currentPage: 1,
    records: []
  }
}

// ========== 根据值查找所对应的项目名称 ==========
/**
 * @param sourceRecord 查找对象
 * @param targetValue 查找的值
 */
export const findKeyByValue = (
  sourceRecord:Record<any,any>,
  targetValue: number) => {
  return Object.keys(sourceRecord).find(
    key => sourceRecord[key] === targetValue
  )
}

// ========== 获取当前教学月份 ==========
export const getCurrentTeachingMonth=():string=>{
  const currentDate = dayjs()
  // 如果当前日期小于5，减一个月，否则不减
  const targetDate = currentDate.date() < 5
    ? currentDate.subtract(1, 'month')
    : currentDate
  return targetDate.format('YYYY-MM')
}

// ========== 禁用非当前月份的日期 ==========
/**
 * @param teachingMonth 当前月份
 */
export const getDisabledDate = (teachingMonth:string) => {
  if(teachingMonth){
    const [year, month] = teachingMonth.split('-').map(Number)
    return (date) => {
      return date.getFullYear() !== year || date.getMonth() !== month - 1
    }
  }
  const today = new Date()
  const currentDay = today.getDate()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() // 0-11
  // 判断是否小于本月26号
  const isBefore6 = currentDay < 26
  return (date) => {
    const year = date.getFullYear()
    const month = date.getMonth() // 0-11
    if (isBefore6) {
      // 小于6号：只允许选择上月和本月
      // 本月
      const isCurrentMonth = year === currentYear && month === currentMonth
      // 上月
      let lastMonthYear = currentYear
      let lastMonth = currentMonth - 1
      if (lastMonth < 0) {
        lastMonth = 11
        lastMonthYear = currentYear - 1
      }
      const isLastMonth = year === lastMonthYear && month === lastMonth
      // 禁用除了上月和本月以外的所有月份
      return !(isCurrentMonth || isLastMonth)
    } else {
      // 大于等于6号：只允许选择本月
      const isCurrentMonth = year === currentYear && month === currentMonth
      return !isCurrentMonth
    }
  }
}

// ========== 获取学校列表 ==========
export const getSchoolList = async (
  formItems:any[],
  item:string,
  role: string = 'admin',
)=>{
  return await updateFormSelect(
    formItems,
    item,
    {
      url: `/${role}/getSchoolList`,
      labelKey:'name',
      valueKey:'id',
      errorMessage: '获取用户年级列表失败'
    })
}

// ========== 获取班级列表 ==========
export const getClassNumberList = async (
  formItems:any[],
  item:string,
  school:string,
  role?:string
)=>{
  return await updateFormSelect(
    formItems,
    item,
    {
      url: role==='admin'?'/admin/getClassNumber':'/user/getUserClassNumber',
      labelKey: 'name',
      valueKey:'id',
      params:{
        school:school,
      },
      errorMessage: '获取班级列表失败'
    }
  )
}

// ========== 获取选科组合列表 ==========
export const getSubjectGroup = async (
  formItems:any[],
  item:string,
  role: string = 'admin',
)=>{
  return await updateFormSelect(
    formItems,
    item,
    {
      url:`${role}/getSubjectGroupsInfo`,
      labelKey:'name',
      valueKey:'id',
      errorMessage: '获取用户年级列表失败'
    })
}

// ========== 对字符串数字进行排序 ==========
export const sortString = (targetString:any)=>{
  // 如果输入为空，直接返回空字符串
  if (!targetString || (Array.isArray(targetString) && targetString.length === 0)) return ''
  // 转换为数组
  const arr = Array.isArray(targetString)
    ? targetString
    : targetString.split(',').map((s: string) => s.trim())
  // 过滤、转换、排序
  const result = arr
    .filter(Boolean)  // 过滤掉空字符串、null、undefined
    .map(Number)
    .filter(n => !isNaN(n))
    .sort((a, b) => a - b)
  return result.length ? result : ''
}

// ========== 获取学生类别 ==========
export const getCategoryName = (category) => {
  if(category === -1) return ''
  if (category == 0) return '文化';
  if (category == 1) return '艺术';
  return '体育';
}

// ========== 清除所有数据 ==========
export const deleteAllData=async (type:number):void => {
  const result= await getRequestData('admin/deleteAllData',{
    type:type
  })
  if(result.status) successMessage('清除数据成功')
}
